import React from "react";
import { GroupActivityInline } from "@/main/react/group/GroupActivityLine";
import { DiscussionWithoutContentDTO, GroupDTO, SeriesDTO, UserProfileDTO } from "@/generated-client";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import { Localizer } from "@/main/react/Types";
import { type AnalyticsDiscoveryTarget, type AnalyticsSurface } from "@/main/analytics/analyticsEvents";
import { onDiscoveryCardActivated } from "@/main/analytics/discoveryAttribution";
import { useDiscoveryRailImpression } from "@/main/react/hooks/useDiscoveryRailImpression";
import { getUserFacingSeriesDescription, getUserFacingSeriesTitle } from "@/main/react/series/BookmarksSeriesLocalization";
import { History, List, RefreshCw, UserSquare2, Users } from "lucide-react";
import { type HomeFeedType, LoadableItems } from "./HomePageShared";
import { WarningBanner } from "@/main/react/component/WarningBanner";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import { getDisplayUsername, getUserProfilePath, normalizeDisplayUsername, hasDisplayUsername} from "@/main/common/UsernameDisplayUtils";
import { RecentlyViewedHistoryPanel } from "@/main/react/page/reading-history/RecentlyViewedHistoryPanel";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import { cn } from "@/lib/utils";
import GroupButtons from "@/main/react/group/GroupButtons";
import SeriesButtons from "@/main/react/series/SeriesButtons";
import { UserButtons } from "@/main/react/user/UserButtons";
import { AdSlot } from "@/main/react/ads/AdSlot";

const HOME_DISCOVERY_REVEAL_DURATION_MS = 220;
type HomeDiscoveryTabKey = "yourGroups" | "groups" | "series" | "users" | "history";
type HomeDiscoveryCardLayout = "rail" | "inline";

function getRecommendationTitleClassName(layout: HomeDiscoveryCardLayout) {
    return cn(
        "text-sm font-semibold text-text-main wrap-anywhere",
        layout === "inline" ? "line-clamp-2 leading-5" : "line-clamp-2 leading-5",
    );
}

function getRecommendationDescriptionClassName(layout: HomeDiscoveryCardLayout) {
    return cn(
        "text-xs text-muted-foreground wrap-anywhere",
        layout === "inline" ? "mt-1.5 line-clamp-2 leading-4.5" : "mt-2 line-clamp-3 leading-5",
    );
}

/**
 * `HOME-FEED-32` — the tile/text layout of the three INLINE (carousel-slide) recommendation cards.
 *
 * `items-start`, matching `HomeRailRecommendationRow` above, which has always used it. All three
 * inline cards used `items-center`, and on a 412px slide the text column is ~130 CSS px tall — a
 * two-line title, a visibility line, a two-line description and a stats row — so the 40px tile ended
 * up vertically centred against it, sitting beside the DESCRIPTION rather than beside the name it is
 * the label for, with visible void above and below. The avatar is the title's label; centring it
 * breaks that pairing and leaves the slide's left column reading as half-empty.
 *
 * One constant rather than three literals: this is one decision about one card family, and three
 * copies is how the next card added to that family comes back centred.
 */
const INLINE_RECOMMENDATION_CARD_BODY_CLASS = "flex items-start gap-2.5";

function HomeRailSection(props: {
    loading: boolean;
    loadingLabel: string;
    error: string | null;
    empty: boolean;
    onRetry?: () => void;
    retryLabel?: string;
    children: React.ReactNode;
}) {
    const { loading, loadingLabel, error, empty, onRetry, retryLabel, children } = props;

    if (!loading && !error && empty) {
        return null;
    }

    return (
        <div>
            {loading ? <div className="text-sm text-neutral-text">{loadingLabel}</div> : null}
            {!loading && error ? (
                <div className="flex flex-col items-start gap-2">
                    <WarningBanner description={error} className="py-3" />
                    {onRetry ? (
                        <CtaButton
                            icon={RefreshCw}
                            text={retryLabel ?? ""}
                            size="sm"
                            variant="outline"
                            onClick={onRetry}
                        />
                    ) : null}
                </div>
            ) : null}
            {!loading && !error ? children : null}
        </div>
    );
}

/**
 * One recommendation as a FLUSH row — no card surface of its own.
 *
 * <p>Until 2026-08-09 each of these was a `rounded-2xl border bg-muted/55 p-3` card sitting inside the
 * rail panel, which is itself a bordered card inside a padded Accordion: three nested padded surfaces to
 * render one row, putting the title 108px from the column's left edge in a column that is already narrow.
 * The operator asked for the text to start at the left and for the vertical waste to go.
 *
 * <p>**Why the anchor is split rather than wrapping everything.** The action (Join/Follow) now sits inline
 * on the meta row, and an interactive control nested inside an `<a>` is invalid markup that also swallows
 * the click — the old layout paid for that with a whole bordered footer row. So the anchor wraps the title
 * block only, and the meta row is its sibling. Counts are not link text, so nothing is lost from the
 * accessible name. The tile gets its own anchor purely so it stays clickable; it is `aria-hidden` and out
 * of the tab order, so the row still exposes exactly ONE link.
 *
 * <p>Design: designs/01-now/2026-08-09-sidebar-single-scroll-and-home-rail-flush-rows-design.md §2.
 */
function HomeRailRecommendationRow(props: {
    href?: string | null;
    playwrightId: string;
    targetType?: AnalyticsDiscoveryTarget;
    surface?: AnalyticsSurface;
    tile: React.ReactNode;
    children: React.ReactNode;
    meta?: React.ReactNode;
    action?: React.ReactNode;
}) {
    const { href, playwrightId, targetType, surface, tile, children, meta, action } = props;
    const onCardClick = () => {
        if (targetType) {
            onDiscoveryCardActivated({ targetType, surface: surface ?? "home_rail" });
        }
    };

    return (
        <div
            // `-mx-2 px-2` gives the hover wash and the divider a little bleed past the text WITHOUT
            // indenting the text itself — the whole point of the change is that the copy starts at the
            // panel's own content edge.
            className="-mx-2 flex items-start gap-3 rounded-lg px-2 py-3 transition-colors first:pt-0 last:pb-0 hover:bg-muted/45"
            data-playwright-id={playwrightId}
            data-home-discovery-card-effect="browse"
        >
            {href ? (
                <a href={href} className="shrink-0" onClick={onCardClick} tabIndex={-1} aria-hidden="true">
                    {tile}
                </a>
            ) : (
                <div className="shrink-0">{tile}</div>
            )}
            <div className="min-w-0 flex-1">
                {href ? (
                    <a href={href} className="block" onClick={onCardClick}>
                        {children}
                    </a>
                ) : (
                    <div>{children}</div>
                )}
                {meta || action ? (
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                        <div className="flex min-w-0 flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                            {meta}
                        </div>
                        {action ? <div className="shrink-0">{action}</div> : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

/**
 * The MOBILE presentation — a fixed-height slide in the horizontal carousel
 * (`HomeMobileDiscoveryRailSection`). Deliberately still a card: the surface is the slide affordance, and
 * none of the desktop rail's complaints (a narrow lateral column, stacked vertical waste) apply here.
 */
function HomeRecommendationCardShell(props: {
    href?: string | null;
    playwrightId: string;
    targetType?: AnalyticsDiscoveryTarget;
    surface?: AnalyticsSurface;
    children: React.ReactNode;
    action?: React.ReactNode;
}) {
    const { href, playwrightId, targetType, surface, children, action } = props;
    const onCardClick = () => {
        if (targetType) {
            onDiscoveryCardActivated({ targetType, surface: surface ?? "mobile_inline" });
        }
    };
    const surfaceClassName = "flex h-full min-h-[12.75rem] flex-col rounded-2xl border border-border/60 bg-muted/55 p-3.5";

    const content = href ? (
        <a href={href} className="flex flex-1 flex-col justify-start" onClick={onCardClick}>
            {children}
        </a>
    ) : (
        <div className="flex flex-1 flex-col justify-start">{children}</div>
    );

    return (
        <div
            className={surfaceClassName}
            data-playwright-id={playwrightId}
            data-home-discovery-card-effect="browse"
        >
            {content}
            {action ? (
                <div className="mt-2.5 border-t border-border/50 pt-2.5">{action}</div>
            ) : null}
        </div>
    );
}

/**
 * `HOME-FEED-27` (operator decision **D25**) — one card, two jobs, and the difference is not cosmetic.
 *
 * <ul>
 *   <li><b>`recommendation`</b> (default, unchanged): a group the member is NOT in. It carries
 *       `GroupButtons` so Join is one tap away, and it reports into the discovery instrument.</li>
 *   <li><b>`membership`</b>: a group the member IS already in, rendered by the "My groups" module.
 *       Two suppressions, both deliberate. The ACTION is dropped because `GroupButtons` resolves
 *       `initiallyMember` from the DTO and would therefore render <b>Leave</b> as the primary control
 *       on a module whose entire job is to get the member back INTO a group. And the DISCOVERY
 *       ANALYTICS are dropped (`targetType` is left undefined, so `onDiscoveryCardActivated` never
 *       fires) because this is not a recommendation: counting it would put clicks in the numerator of
 *       `discovery_card_clicked{target_type=group}` that the impression denominator does not contain,
 *       which is worse than not measuring it at all. Measuring the membership module properly is owed
 *       work and is recorded on the row, not smuggled into the recommendation instrument.</li>
 * </ul>
 */
export type RecommendedGroupCardMode = "recommendation" | "membership";

export function RecommendedGroupCard(props: { group: GroupDTO; localizer: Localizer; layout?: HomeDiscoveryCardLayout; surface?: AnalyticsSurface; mode?: RecommendedGroupCardMode }) {
    const { group, localizer, layout = "rail", surface, mode = "recommendation" } = props;
    const isMembershipCard = mode === "membership";
    const visibilityLabel = Boolean(group.privateAccess) ? localizer("group.private") : localizer("group.public");
    const groupPathTarget = group.slug?.trim() || group.publicId?.trim();
    const groupHref = groupPathTarget ? `/group/${encodeURIComponent(groupPathTarget)}` : null;

    const tile = (
        <ProfileImage localizer={localizer}
            imageUrl={group.thumbnailUrl}
            username={group.name ?? ""}
            mode="group"
            hoverable={false}
            // ⚠️ This comment used to claim the squared tile matched "the series and
            // related-discussion tiles this card sits beside". It does not, and has not:
            // `RecommendedSeriesCard` below passes no `shape`, so `ProfileImage` defaults to
            // `circle`, and `ActivePublisherCard` is a person's avatar, which is circular
            // everywhere in the product. So the rail genuinely renders two shapes in one tile
            // column and "one tile column, one shape" cannot be satisfied by moving the series
            // tile alone. Left as-is deliberately (2026-08-09) — picking the column's shape is a
            // product decision, parked as `home-recommendation-group-tile-shape-parity` in
            // designs/07-archive/runs/2026-08-03-ux-refine-cards-run1-design.md. Flush rows line the tiles
            // up in one uninterrupted column, so the mismatch is now easier to see, not harder.
            shape="rounded"
            className={cn("shrink-0", layout === "inline" ? "size-10" : "size-12")}
        />
    );

    const titleBlock = (
        <>
            <div className={getRecommendationTitleClassName(layout)}>{group.name}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">{visibilityLabel}</div>
            {group.description?.trim() ? (
                <p className={getRecommendationDescriptionClassName(layout)}>{group.description}</p>
            ) : null}
        </>
    );

    const meta = (
        <>
            {/* Real ICU plurals rather than `{n} {noun}` — the concatenated form rendered "1 posts"
                (`DISCUSSION-20`) and cannot be made correct at all in a locale with more than two
                plural forms, because the count and the noun are separate tokens. */}
            <GroupActivityInline group={group} localizer={localizer} />
            <span>{localizer("badge.group.discussions", String(Number(group.discussionsCount ?? 0)))}</span>
        </>
    );

    const action = group.publicId && !isMembershipCard ? (
        <GroupButtons
            groupPublicId={group.publicId}
            groupSlug={group.slug ?? groupPathTarget ?? group.publicId}
            initiallyMember={Boolean(group.isUserPartOfGroup)}
            initiallyLeader={Boolean(group.isUserLeaderOfGroup)}
            initiallyBanned={Boolean(group.isUserBanned)}
            initiallyRequestedToJoin={Boolean(group.isUserRequestedToJoinGroup)}
            groupName={group.name ?? ""}
            groupDescription={group.description ?? ""}
            groupThumbnailUrl={group.thumbnailUrl ?? ""}
            mainLocale={group.mainLocale ?? undefined}
            privateAccess={Boolean(group.privateAccess)}
            sensitive={Boolean(group.sensitive)}
            compact={true}
            displayMode="primary-only"
            surface="home_rail"
            localizer={localizer}
        />
    ) : undefined;

    if (layout === "rail") {
        return (
            <HomeRailRecommendationRow
                href={groupHref}
                targetType={isMembershipCard ? undefined : "group"}
                surface={surface}
                playwrightId={isMembershipCard ? "home-your-group-card" : "home-recommended-group-card"}
                tile={tile}
                meta={meta}
                action={action}
            >
                {titleBlock}
            </HomeRailRecommendationRow>
        );
    }

    return (
        <HomeRecommendationCardShell
            href={groupHref}
            targetType={isMembershipCard ? undefined : "group"}
            surface={surface}
            playwrightId={isMembershipCard ? "home-your-group-card" : "home-recommended-group-card"}
            action={action}
        >
            <div className={INLINE_RECOMMENDATION_CARD_BODY_CLASS}>
                {tile}
                <div className="min-w-0 flex-1">
                    {titleBlock}
                    <div className="mt-2 flex flex-wrap gap-x-2.5 gap-y-1 text-xs text-muted-foreground">
                        {meta}
                    </div>
                </div>
            </div>
        </HomeRecommendationCardShell>
    );
}

export function RecommendedSeriesCard(props: { series: SeriesDTO; localizer: Localizer; layout?: HomeDiscoveryCardLayout }) {
    const { series, localizer, layout = "rail" } = props;
    const displayTitle = getUserFacingSeriesTitle(series, localizer);
    const displayDescription = getUserFacingSeriesDescription(series, localizer);
    const displayOwnerUsername = getDisplayUsername(series.ownerUsername, localizer);
    const ownerProfilePath = getUserProfilePath(series.ownerUsername, "discussions");
    const visibilityLabel = Boolean(series.privateAccess) ? localizer("series.private") : localizer("series.public");
    const seriesPathTarget = series.slug?.trim() || series.publicId?.trim();
    const seriesHref = seriesPathTarget ? `/series/${encodeURIComponent(seriesPathTarget)}` : null;
    const attributionHref = seriesPathTarget ? null : ownerProfilePath;

    const tile = (
        <ProfileImage localizer={localizer}
            imageUrl={series.thumbnailUrl}
            username={displayTitle}
            // `LANDING-64`. This used to be `mode="group"`, so a series with no cover art fell back to
            // the two-person Users glyph — byte-identical to the placeholder the group rows twelve lines
            // above use. Inside one module the heading said "series" and the row said "group", on
            // exactly the rows that have no photograph to carry the meaning instead.
            //
            // `mode` selects the FALLBACK ICON and nothing else; `ProfileImage`'s own prop doc states
            // that shape is deliberately independent of it. This card passes no `shape`, so the tile
            // stays a circle and the parked `home-recommendation-group-tile-shape-parity` decision is
            // untouched — only the glyph changes, to the same `List` mark the module heading uses.
            mode="series"
            hoverable={false}
            className={cn("shrink-0", layout === "inline" ? "size-10" : "size-12")}
        />
    );

    const titleBlock = (
        <>
            <div className={getRecommendationTitleClassName(layout)}>{displayTitle}</div>
            <AuthorAttribution localizer={localizer}
                name={displayOwnerUsername}
                href={attributionHref}
                imageUrl={series.ownerProfileImageUrl}
                avatarSeed={series.ownerUsername ?? displayOwnerUsername}
                details={visibilityLabel}
                className={cn("text-xs text-neutral-text", layout === "inline" ? "mt-0.5" : "mt-1")}
                avatarClassName="size-4.5"
            />
            {displayDescription ? (
                <p className={getRecommendationDescriptionClassName(layout)}>{displayDescription}</p>
            ) : null}
        </>
    );

    const meta = (
        <>
            {/* Same plural fix as the group rows above (`DISCUSSION-20`). */}
            <span>{localizer("badge.series.discussions", String(Number(series.discussionsCount ?? 0)))}</span>
            <span>{localizer("badge.series.followers", String(Number(series.followersCount ?? 0)))}</span>
        </>
    );

    const action = series.publicId && !series.isViewerOwner ? (
        <SeriesButtons
            seriesPublicId={series.publicId}
            seriesSlug={series.slug ?? seriesPathTarget ?? series.publicId}
            initiallyOwner={Boolean(series.isViewerOwner)}
            initiallyFollowing={Boolean(series.isViewerFollowing)}
            seriesTitle={series.title ?? ""}
            seriesDescription={series.description ?? ""}
            seriesThumbnailUrl={series.thumbnailUrl ?? ""}
            mainLocale={series.mainLocale ?? undefined}
            privateAccess={Boolean(series.privateAccess)}
            bookmarksCollection={Boolean(series.bookmarksCollection)}
            sensitive={Boolean(series.sensitive)}
            allowWrap={false}
            displayMode="primary-only"
            surface="home_rail"
            localizer={localizer}
        />
    ) : undefined;

    if (layout === "rail") {
        return (
            <HomeRailRecommendationRow
                href={seriesHref}
                targetType="series"
                playwrightId="home-recommended-series-card"
                tile={tile}
                meta={meta}
                action={action}
            >
                {titleBlock}
            </HomeRailRecommendationRow>
        );
    }

    return (
        <HomeRecommendationCardShell
            href={seriesHref}
            targetType="series"
            playwrightId="home-recommended-series-card"
            action={action}
        >
            <div className={INLINE_RECOMMENDATION_CARD_BODY_CLASS}>
                {tile}
                <div className="min-w-0 flex-1">
                    {titleBlock}
                    <div className="mt-2 flex flex-wrap gap-x-2.5 gap-y-1 text-xs text-neutral-text">
                        {meta}
                    </div>
                </div>
            </div>
        </HomeRecommendationCardShell>
    );
}

export function ActivePublisherCard(props: { user: UserProfileDTO; localizer: Localizer; layout?: HomeDiscoveryCardLayout }) {
    const { user, localizer, layout = "rail" } = props;
    const normalizedUsername = normalizeDisplayUsername(user.username);
    const displayUsername = getDisplayUsername(user.username, localizer);
    const profileUrl = getUserProfilePath(user.username, "discussions");

    const tile = (
        <ProfileImage localizer={localizer}
            imageUrl={user.profileImageUrl}
            username={normalizedUsername ?? undefined}
            mode="user"
            hoverable={false}
            className={cn("shrink-0", layout === "inline" ? "size-10" : "size-12")}
        />
    );

    const titleBlock = (
        <>
            <div className={getRecommendationTitleClassName(layout)}
                 translate={hasDisplayUsername(user.username) ? "no" : undefined}>{displayUsername}</div>
            {user.description?.trim() ? (
                <p className={getRecommendationDescriptionClassName(layout)}>{user.description}</p>
            ) : null}
        </>
    );

    const meta = (
        <>
            {/* `HOME-FEED-23` / `DISCUSSION-20`. This used to be `{n} {localizer("user.discussions")}` —
                a count and a bare plural noun as separate tokens, which renders "1 posts" and CANNOT be
                repaired by translation: in Polish, Russian, Romanian and Arabic no single value for a
                standalone noun token is correct across the count's plural categories.

                `badge.user.discussions` / `badge.user.followers` are real ICU plurals and already
                existed in all 25 catalogs, so this cost no new key. The register priced it as an
                "add-and-migrate" because `user.discussions` / `user.followers` have other call sites —
                that is true and irrelevant: those keys are untouched and keep them. The sibling
                `RecommendedGroupCard` above already renders this shape. */}
            <span>{localizer("badge.user.discussions", String(Number(user.discussionsAuthoredCount ?? 0)))}</span>
            <span>{localizer("badge.user.followers", String(Number(user.followersCount ?? 0)))}</span>
        </>
    );

    const action = normalizedUsername ? (
        <UserButtons
            username={normalizedUsername}
            initiallyFollowing={Boolean(user.isViewerFollowing)}
            allowWrap={false}
            displayMode="primary-only"
            surface="home_rail"
            localizer={localizer}
        />
    ) : undefined;

    if (layout === "rail") {
        return (
            <HomeRailRecommendationRow
                href={profileUrl}
                targetType="publisher"
                playwrightId="home-active-publisher-card"
                tile={tile}
                meta={meta}
                action={action}
            >
                {titleBlock}
            </HomeRailRecommendationRow>
        );
    }

    return (
        <HomeRecommendationCardShell
            href={profileUrl}
            targetType="publisher"
            playwrightId="home-active-publisher-card"
            action={action}
        >
            <div className={INLINE_RECOMMENDATION_CARD_BODY_CLASS}>
                {tile}
                <div className="min-w-0 flex-1">
                    {titleBlock}
                    <div className="mt-2 flex flex-wrap gap-x-2.5 gap-y-1 text-xs text-neutral-text">
                        {meta}
                    </div>
                </div>
            </div>
        </HomeRecommendationCardShell>
    );
}

export function HomeDiscoveryRail(props: {
    historyState: LoadableItems<DiscussionWithoutContentDTO>;
    groupsState: LoadableItems<GroupDTO>;
    seriesState: LoadableItems<SeriesDTO>;
    usersState: LoadableItems<UserProfileDTO>;
    /**
     * `HOME-FEED-27` (**D25**) — which feed the member is reading, so the rail can answer "what is
     * happening in what I already joined?" on the one feed that is named after their groups.
     *
     * Optional, and absent means `home`: every other caller of this component keeps today's rail
     * exactly. Nothing else in here branches on it.
     */
    feedType?: HomeFeedType;
    /**
     * `HOME-FEED-27` (**D25**) — the member's OWN groups. Loaded by `HomePage` and only on the
     * `groups` feed, so the default feed pays no request for it (`HOME-FEED-9`'s constraint that
     * nothing new may land on the startup path).
     */
    yourGroupsState?: LoadableItems<GroupDTO>;
    localizer: Localizer;
    onRetry?: () => void;
}) {
    const { historyState, groupsState, seriesState, usersState, feedType = "home", yourGroupsState, localizer, onRetry } = props;
    const retryLabel = localizer("literal.retry");
    const [openTabs, setOpenTabs] = React.useState<HomeDiscoveryTabKey[]>([]);
    const [recentlyOpenedTabKey, setRecentlyOpenedTabKey] = React.useState<HomeDiscoveryTabKey | null>(null);
    const revealTimeoutRef = React.useRef<number | null>(null);
    const isVisible = React.useCallback(<T,>(state: LoadableItems<T>) => {
        return state.loading || Boolean(state.error) || state.items.length > 0;
    }, []);
    const tabs = React.useMemo(() => {
        return [
            /**
             * `HOME-FEED-27` (operator decision **D25**) — the member's view of their own groups.
             *
             * The row that produced this: a feed named after 32 group memberships rendered the groups
             * themselves nowhere, while the one group-shaped module on the page asked that member to
             * join more. The feed answered "what could you add?" and nothing answered "what is
             * happening in what you already joined?".
             *
             * <p><b>Gated on the `groups` feed, not on the account.</b> This is the member's view of
             * ONE feed, not a permanent piece of chrome — on `home` the rail is unchanged, which is
             * also what keeps the request off the default page.
             *
             * <p><b>First in the array, so it sits ABOVE "Groups you may like".</b> Since
             * `HOME-FEED-45` (2026-09-24) nothing is hoisted above it: the order on the groups feed is
             * your groups, groups you may like, people to follow, series, continue reading.
             *
             * <p><b>`items.length > 0`, not `isVisible`.</b> `isVisible` is also true in flight and on
             * error, and a spinner shown above the acquisition modules and then withdrawn is a layout
             * shift in the rail's best slot.
             */
            {
                key: "yourGroups" as const,
                label: localizer("sidebar.groups"),
                subtitle: localizer("home.your-groups.subtitle"),
                icon: Users,
                playwrightId: "home-your-groups-section",
                visible: feedType === "groups" && (yourGroupsState?.items.length ?? 0) > 0,
                content: (
                    <div>
                        {(yourGroupsState?.items ?? []).map((group) => (
                            <RecommendedGroupCard
                                key={group.publicId ?? group.slug ?? group.name ?? "your-group"}
                                group={group}
                                localizer={localizer}
                                mode="membership"
                            />
                        ))}
                        {/* Round 38 D32 (HOME-FEED-50): the request behind this module caps at 20 rows,
                            so the module ends with the way to all of them. A real link (a navigation). */}
                        <a
                            href="/my_dashboard/groups/member"
                            data-playwright-id="home-your-groups-see-all"
                            className="mt-2 inline-flex min-h-9 items-center rounded-full px-3 text-sm font-medium text-primary underline-offset-4 hover:underline"
                        >
                            {localizer("home.groups_rail.see_all")}
                        </a>
                    </div>
                ),
            },
            {
                key: "groups" as const,
                label: localizer("home.groups.title"),
                subtitle: localizer("home.groups.subtitle"),
                icon: Users,
                playwrightId: "home-groups-section",
                visible: isVisible(groupsState),
                content: (
                    <HomeRailSection
                        loading={groupsState.loading}
                        loadingLabel={localizer("home.loading")}
                        error={groupsState.error}
                        empty={groupsState.items.length === 0}
                        onRetry={onRetry}
                        retryLabel={retryLabel}
                    >
                        <div>
                            {groupsState.items.map((group) => (
                                <RecommendedGroupCard
                                    key={group.publicId ?? group.slug ?? group.name ?? "group"}
                                    group={group}
                                    localizer={localizer}
                                />
                            ))}
                        </div>
                    </HomeRailSection>
                ),
            },
            {
                key: "users" as const,
                label: localizer("home.publishers.title"),
                subtitle: localizer("home.publishers.subtitle"),
                icon: UserSquare2,
                playwrightId: "home-publishers-section",
                visible: isVisible(usersState),
                content: (
                    <HomeRailSection
                        loading={usersState.loading}
                        loadingLabel={localizer("home.loading")}
                        error={usersState.error}
                        empty={usersState.items.length === 0}
                        onRetry={onRetry}
                        retryLabel={retryLabel}
                    >
                        <div>
                            {usersState.items.map((user) => (
                                <ActivePublisherCard
                                    key={user.username ?? "user"}
                                    user={user}
                                    localizer={localizer}
                                />
                            ))}
                        </div>
                    </HomeRailSection>
                ),
            },
            {
                key: "series" as const,
                label: localizer("home.series.badge"),
                title: localizer("home.series.title"),
                subtitle: localizer("home.series.subtitle"),
                icon: List,
                playwrightId: "home-series-section",
                visible: isVisible(seriesState),
                content: (
                    <HomeRailSection
                        loading={seriesState.loading}
                        loadingLabel={localizer("home.loading")}
                        error={seriesState.error}
                        empty={seriesState.items.length === 0}
                        onRetry={onRetry}
                        retryLabel={retryLabel}
                    >
                        <div>
                            {seriesState.items.map((series, index) => (
                                <RecommendedSeriesCard
                                    key={series.publicId ?? series.slug ?? series.title ?? `series-${index}`}
                                    series={series}
                                    localizer={localizer}
                                />
                            ))}
                        </div>
                    </HomeRailSection>
                ),
            },
            {
                key: "history" as const,
                label: localizer("home.continue_reading.title"),
                subtitle: localizer("home.continue_reading.subtitle"),
                icon: History,
                playwrightId: "home-continue-reading-section",
                visible: isVisible(historyState),
                content: (
                    <HomeRailSection
                        loading={historyState.loading}
                        loadingLabel={localizer("home.loading")}
                        error={historyState.error}
                        empty={historyState.items.length === 0}
                        onRetry={onRetry}
                        retryLabel={retryLabel}
                    >
                        <RecentlyViewedHistoryPanel
                            items={historyState.items}
                            localizer={localizer}
                        />
                    </HomeRailSection>
                ),
            },
        ];
    }, [feedType, groupsState, historyState, isVisible, localizer, onRetry, retryLabel, seriesState, usersState, yourGroupsState]);
    // `HOME-FEED-45` (operator requirement, 2026-09-24) — the rail's order is FIXED and is the
    // `tabs` literal above: (your groups on the Groups feed,) groups you may like, people to follow,
    // recommended series, continue reading. Standing principle
    // (designs/implementation-map/discovery.md): groups get priority for space in home discovery.
    //
    // This deliberately reverses `HOME-FEED-4`, which hoisted "Continue reading" to the top whenever
    // the member had history. Do not re-introduce a hoist above groups; the guard is
    // `HomeDiscoveryRailModuleOrder.spec.tsx`.
    //
    // MEASUREMENT: moving a module changes the denominator of
    // `discovery_card_clicked{surface=home_rail}` (an impression is counted only once a module is
    // expanded, resolved and scrolled to). Both breaks, 2026-08-20 (the hoist) and 2026-09-24 (its
    // reversal), are recorded in designs/01-now/2026-08-07-recommendation-surface-measurement-design.md
    // §"Measurement discontinuities", whose §Decision 1 owns this instrument.
    const availableTabs = React.useMemo(() => tabs.filter((tab) => tab.visible), [tabs]);
    const availableTabKeys = React.useMemo<HomeDiscoveryTabKey[]>(() => availableTabs.map((tab) => tab.key), [availableTabs]);

    // The denominator for `discovery_card_clicked{surface=home_rail}`. One impression per module per
    // page instance, and only once that module is expanded, resolved and non-empty — see
    // designs/01-now/2026-08-07-recommendation-surface-measurement-design.md §Decision 1. Without this
    // the series rail's 10 clicks in 30 days cannot be told apart from a rail that rarely renders.
    const isModuleReady = React.useCallback(
        <T,>(key: HomeDiscoveryTabKey, state: LoadableItems<T>) =>
            openTabs.includes(key) && !state.loading && !state.error,
        [openTabs],
    );
    useDiscoveryRailImpression({
        targetType: "group",
        surface: "home_rail",
        itemCount: groupsState.items.length,
        ready: isModuleReady("groups", groupsState),
    });
    useDiscoveryRailImpression({
        targetType: "series",
        surface: "home_rail",
        itemCount: seriesState.items.length,
        ready: isModuleReady("series", seriesState),
    });
    useDiscoveryRailImpression({
        targetType: "publisher",
        surface: "home_rail",
        itemCount: usersState.items.length,
        ready: isModuleReady("users", usersState),
    });
    useDiscoveryRailImpression({
        targetType: "discussion",
        surface: "home_rail",
        itemCount: historyState.items.length,
        ready: isModuleReady("history", historyState),
    });

    const clearRevealTimeout = React.useCallback(() => {
        if (revealTimeoutRef.current != null) {
            window.clearTimeout(revealTimeoutRef.current);
            revealTimeoutRef.current = null;
        }
    }, []);

    React.useEffect(() => clearRevealTimeout, [clearRevealTimeout]);

    React.useEffect(() => {
        setOpenTabs((previous) => {
            const previousSet = new Set(previous);
            const normalizedPrevious = previous.filter((key) => availableTabKeys.includes(key));
            const next = [...normalizedPrevious];

            for (const key of availableTabKeys) {
                if (!previousSet.has(key)) {
                    next.push(key);
                }
            }

            return next;
        });
    }, [availableTabKeys]);

    const handleOpenTabsChange = React.useCallback((nextOpenTabs: string[]) => {
        const normalizedOpenTabs = nextOpenTabs.filter((key): key is HomeDiscoveryTabKey => availableTabKeys.includes(key as HomeDiscoveryTabKey));
        setOpenTabs((previous) => {
            const previousSet = new Set(previous);
            const newlyOpenedTabKey = normalizedOpenTabs.find((key) => !previousSet.has(key)) ?? null;

            clearRevealTimeout();
            if (newlyOpenedTabKey) {
                setRecentlyOpenedTabKey(newlyOpenedTabKey);
                revealTimeoutRef.current = window.setTimeout(() => {
                    setRecentlyOpenedTabKey(null);
                    revealTimeoutRef.current = null;
                }, HOME_DISCOVERY_REVEAL_DURATION_MS);
            } else {
                setRecentlyOpenedTabKey(null);
            }

            return normalizedOpenTabs;
        });
    }, [availableTabKeys, clearRevealTimeout]);

    // HOME-FEED-30. Measured rather than assumed: `scrollHeight > clientHeight` is the only thing that
    // says this rail is clipped, and it changes when a panel opens, when data lands and when the window
    // resizes — so all three are observed. `scrollTop` decides whether the fade is still owed; at the
    // bottom there is nothing below to hint at.
    const railRef = React.useRef<HTMLElement | null>(null);
    const [hasClippedOverflow, setHasClippedOverflow] = React.useState(false);

    React.useEffect(() => {
        const node = railRef.current;
        if (!node) {
            return;
        }

        const measure = () => {
            const overflowing = node.scrollHeight - node.clientHeight > 1;
            const atBottom = node.scrollHeight - node.clientHeight - node.scrollTop <= 1;
            setHasClippedOverflow(overflowing && !atBottom);
        };

        measure();
        node.addEventListener("scroll", measure, { passive: true });

        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
        observer?.observe(node);
        window.addEventListener("resize", measure);

        return () => {
            node.removeEventListener("scroll", measure);
            observer?.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [availableTabs.length, openTabs]);

    if (availableTabs.length === 0) {
        return null;
    }

    return (
        <aside
            ref={railRef}
            className={cn(
                "space-y-4 hidden lg:block  lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto no-scrollbar",
                // HOME-FEED-30: the rail hides its scrollbar by a recorded decision, so overflow was cut
                // flush at the window edge with nothing standing in for the affordance that removal took
                // away — a reader sees a card sliced in half and reads it as the end of the list. The fade
                // is the substitute, and it is conditional on ACTUAL overflow (the same rail does not clip
                // on many pages) and drops away at the bottom so the last row is never dimmed.
                hasClippedOverflow && "home-discovery-rail-fade",
            )}
            data-playwright-id="home-discovery-rail"
            data-rail-overflowing={hasClippedOverflow ? "true" : "false"}
        >
            {/* No horizontal padding on the rail column itself. It used to carry `px-5`, which was the
                outermost of THREE nested paddings (Accordion 20 + AccordionContent 16 + card 12) before a
                recommendation's title started — 108px into a column that is already narrow. The panel's own
                `px-3.5` below is the only inset left. */}
            <Accordion type="multiple" value={openTabs} onValueChange={handleOpenTabsChange} className="space-y-4 pb-5">
                {availableTabs.map((tab) => {
                    const Icon = tab.icon;
                    const title = tab.title ?? tab.label;
                    const isOpen = openTabs.includes(tab.key);
                    const isRecentlyOpened = recentlyOpenedTabKey === tab.key;

                    return (
                        <AccordionItem
                            key={tab.key}
                            value={tab.key}
                            className="border-none"
                            data-playwright-id={tab.playwrightId}
                        >
                            <div
                                className={cn(
                                    // SV-135 S6 (D5, 2026-09-23): a `--divider` edge with no fill and no
                                    // shadow, open or closed. The recommendation rows inside keep their
                                    // sanctioned lift-on-hover (design-language "Sanctioned exceptions"):
                                    // the exception wins for the hover, D5 for this resting edge.
                                    "overflow-hidden rounded-2xl border border-divider",
                                    // `LANDING-12` — the keyboard focus indicator for this section's
                                    // header lives HERE, on the panel, not on the button.
                                    //
                                    // The trigger is a real button that collapses the section, and on
                                    // the anonymous front door a keyboard user tabbing out of the
                                    // header lands on it. `accordion.tsx` supplies the shared indicator
                                    // as `outline-none` plus `focus-visible:ring-[3px] ring-ring/50
                                    // border-ring`, and this call site used to append
                                    // `focus-visible:ring-0`, which won the ring-width slot through
                                    // tailwind-merge. What survived could not paint: a ring COLOUR on a
                                    // zero-width ring, and a `border-ring` COLOUR on a trigger with no
                                    // border-width utility. The stop was invisible.
                                    //
                                    // Simply dropping the override does not fix it, and that is the
                                    // load-bearing part. The trigger's box is the full width of this
                                    // panel, and this panel is `overflow-hidden`, so a restored 3px
                                    // OUTSET ring is drawn entirely outside the panel's content box and
                                    // clipped away — the fix would look applied and paint nothing,
                                    // which is the defect restated. `01-base.css` records the same
                                    // geometry lesson for the card surfaces: inside a clipped ancestor
                                    // the indicator has to be painted INSIDE the border box.
                                    //
                                    // So the indicator is an INSET ring on the panel. It follows the
                                    // panel's own 1.5rem radius, survives both this `overflow-hidden`
                                    // and the rail column's `lg:overflow-y-auto`, and outlines the whole
                                    // section rather than a full-width strip inside it — which is also
                                    // the more honest picture of what Enter is about to collapse.
                                    "has-[[data-slot=accordion-trigger]:focus-visible]:ring-[3px] has-[[data-slot=accordion-trigger]:focus-visible]:ring-ring/50 has-[[data-slot=accordion-trigger]:focus-visible]:ring-inset",
                                )}
                                data-home-reveal-effect="panel"
                                data-home-reveal-open={isOpen ? "true" : "false"}
                                data-home-reveal-phase={isRecentlyOpened ? "settling" : "idle"}
                            >
                                <AccordionTrigger
                                    // `px-5`, matching the content below: `HOME-FEED-7`. The panel's inset moved
                                    // 14px -> 20px so the cards' sanctioned lift-on-hover has room inside the clip
                                    // box. The header moves with them, or the title stops sitting on the same edge
                                    // as the cards. Guard: `HomeDiscoveryRail.spec.tsx`, "HOME-FEED-7".
                                    // `focus-visible:ring-0` is DELIBERATE and must stay: the shared
                                    // indicator would be drawn outside this panel's content box and
                                    // clipped to nothing. The visible indicator is the panel's inset
                                    // ring above (`LANDING-12`). Do not "restore" the ring here.
                                    className="gap-3 px-5 py-3.5 hover:no-underline focus-visible:ring-0 focus-visible:ring-offset-0"
                                    data-playwright-id={`home-discovery-tab-${tab.key}`}
                                >
                                    <div className="flex min-w-0 items-start gap-3 text-left">
                                        <div
                                            className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-text-main"
                                            data-home-reveal-icon-surface="true"
                                        >
                                            <Icon className="size-4.5" />
                                        </div>
                                        <div className="min-w-0" data-home-reveal-copy="true">
                                            <h2 className="text-sm font-semibold text-text-main">{title}</h2>
                                            <p className="text-xs text-neutral-text">{tab.subtitle}</p>
                                        </div>
                                    </div>
                                </AccordionTrigger>
                                {/* `HOME-FEED-7`. This padding is the card's only room: `AccordionContent`
                                    puts this className on an inner div and keeps `overflow-hidden` on the
                                    Radix `Content` above it, so whatever the row's shadow needs has to be
                                    padding here. The rest shadow (`0 10px 24px`) reaches 12px sideways and
                                    22px below; the row bleeds 8px outward, so `px-5` leaves 12 and `pb-6`
                                    leaves 24. `pt-1` covers the 2px `translateY` lift, which `first:pt-0`
                                    on the row otherwise puts flush against the clip edge.
                                    The shadow is NOT the thing to remove — it is a dated, sanctioned
                                    exception (design-language doc, "The home discovery rails carry a
                                    lift-on-hover treatment"). Guard: `HomeDiscoveryRail.spec.tsx`. */}
                                <AccordionContent className="px-5 pt-1 pb-6">
                                    {tab.content}
                                </AccordionContent>
                            </div>
                        </AccordionItem>
                    );
                })}
            </Accordion>

            {/* Desktop-only rail ad below the discovery cards. Renders null (reserving nothing) when
                ads are disabled or consent is not granted; the rail aside is already sticky, so this
                stays in view as the user scrolls. Lateral column → cannot shift main content. */}
            <div className="pb-5">
                <AdSlot placement="rail-rectangle" localizer={localizer} />
            </div>
        </aside>
    );
}
